import React, { useState, useEffect } from 'react';
import { useWebSocket } from './WebSocketContext';
import searchColorGif from '../images/search_color.gif';
import rocket from '../images/rocket.gif';
import loadingGif2 from '../images/astronaut.gif';
import dataunfoldLogo from '../images/logotransparent.png';

import { useTranslation } from "react-i18next";
import { Box, Typography } from '@mui/material';

const Loading = ({ folder }) => {
  const [loadingValue, setLoadingValue] = useState("");
  const [loadingCode, setLoadingCode] = useState(0);
  const { ws, subscribeToMessages } = useWebSocket();
  const { t } = useTranslation();
  const [loadingGif, setLoadingGif] = useState(dataunfoldLogo);
  const [loadingMsg, setLoadingMsg] = useState("Loading...");
  useEffect(() => {
    const unsubscribe = subscribeToMessages((parsedMessage) => {
      // Message is already parsed in WebSocketContext
      if (parsedMessage.command === 'loadingCode') {
        console.log('Code left: ' + parsedMessage.data);
        setLoadingCode(parsedMessage.data);
        switch (parsedMessage.data) {
          case 'EMAILCHECK':
            setLoadingGif(dataunfoldLogo);
            setLoadingMsg("Checking mail...");
            break;
          case 'PREPARE':
            setLoadingGif(dataunfoldLogo);
            setLoadingMsg("Preparing events...");
            break;
          case "LOAD":
            setLoadingGif(dataunfoldLogo);
            setLoadingMsg("Loading events...");
            break;
          default:
            setLoadingGif(dataunfoldLogo);
            setLoadingMsg("Loading...");
        }
      }
      if (parsedMessage.command === 'loadingValue') {
        console.log('File left: ' + parsedMessage.data);
        setLoadingValue(parsedMessage.data);
      }
    });
    return unsubscribe;
  }, [subscribeToMessages]);

  const showFilesLeftText = loadingCode === 'PREPARE' || loadingCode === 'LOAD';

  return (    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '200px',
        borderRadius: 0,
        padding: 3,
        position: 'relative',
        background: 'transparent',
        overflow: 'hidden'
      }}
    >
      {/* Animated background pattern */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: `
          radial-gradient(circle at 30% 20%, rgba(87,87,176,0.15) 0%, transparent 50%),
          radial-gradient(circle at 80% 80%, rgba(87,87,176,0.15) 0%, transparent 50%)
        `,
        animation: 'rotate 20s linear infinite',
        '@keyframes rotate': {
          '0%': { transform: 'rotate(0deg)' },
          '100%': { transform: 'rotate(360deg)' }
        }
      }} />
      
      <Box sx={{ 
        position: 'relative', 
        zIndex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2
      }}>
        {/* Enhanced loading message */}
        <Typography 
          variant="h6" 
          align="center" 
          sx={{ 
            mb: 1,
            color: '#5757b0',
            fontWeight: 'bold',
            textShadow: '0 2px 4px rgba(0,0,0,0.1)',
            animation: 'glow 2s ease-in-out infinite alternate',
            '@keyframes glow': {
              '0%': { textShadow: '0 2px 4px rgba(87,87,176,0.3)' },
              '100%': { textShadow: '0 4px 8px rgba(87,87,176,0.6)' }
            }
          }}
        >
          {`${loadingMsg} ${loadingValue}`}
          {showFilesLeftText && ` ${t("loading.files_left")}.`}
        </Typography>
        
        {/* Enhanced gif container */}
        <Box sx={{ 
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {/* Rotating outer ring */}
          <Box sx={{
            position: 'absolute',
            width: 120,
            height: 120,
            borderRadius: '50%',
            border: '3px solid transparent',
            borderTop: '3px solid #5757b0',
            borderRight: '3px solid rgba(87,87,176,0.3)',
            animation: 'spin 2s linear infinite',
            '@keyframes spin': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' }
            }
          }} />
          
          {/* Inner pulsing ring */}
          <Box sx={{
            position: 'absolute',
            width: 100,
            height: 100,
            borderRadius: '50%',
            border: '2px solid rgba(87,87,176,0.2)',
            animation: 'pulse 1.5s ease-in-out infinite',
            '@keyframes pulse': {
              '0%, 100%': { transform: 'scale(1)', opacity: 0.5 },
              '50%': { transform: 'scale(1.05)', opacity: 0.8 }
            }
          }} />
          
          {/* Gif with enhanced styling */}
          <Box sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255,255,255,0.9)',
            boxShadow: '0 8px 16px rgba(87,87,176,0.2)',
            animation: 'float 3s ease-in-out infinite',
            '@keyframes float': {
              '0%, 100%': { transform: 'translateY(0px)' },
              '50%': { transform: 'translateY(-5px)' }
            }
          }}>            <img 
              src={loadingGif} 
              alt="Loading..." 
              style={{ 
                width: '60px', 
                height: '60px',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
              }} 
            />
          </Box>
        </Box>
        
        {/* Progress indicator dots */}
        <Box sx={{ 
          display: 'flex', 
          gap: 1,
          mt: 1
        }}>
          {[1, 2, 3].map((dot) => (
            <Box
              key={dot}
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: '#5757b0',
                animation: `bounce 1.4s ease-in-out infinite ${dot * 0.2}s`,
                '@keyframes bounce': {
                  '0%, 60%, 100%': { transform: 'translateY(0)' },
                  '30%': { transform: 'translateY(-10px)' }
                }
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default Loading;
